import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import EnterpriseModal from '../EnterpriseModal';
import { isRtl } from '../../i18n/direction';
import api from '../../services/api';

/**
 * ClientQuickAddModal — lightweight "new client" dialog for the therapy
 * dashboard quick action. Bridges to POST /students (or /clients) and
 * returns the new record to the caller.
 */
export default function ClientQuickAddModal({ isOpen, onClose, onSuccess, endpoint = '/students' }) {
  const { t, i18n } = useTranslation();
  const rtl = isRtl(i18n.language);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const reset = () => {
    setName('');
    setPhone('');
    setEmail('');
    setError('');
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) {
      setError(t('validation.required', 'This field is required'));
      return;
    }
    setSubmitting(true);
    try {
      const resp = await api.post(endpoint, {
        name: name.trim(),
        phone: phone.trim() || null,
        email: email.trim() || null,
      });
      onSuccess?.(resp?.data);
      reset();
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '0.5rem 0.75rem',
    borderRadius: '8px',
    border: '1px solid var(--color-border, #d1d5db)',
    fontSize: '0.875rem',
    direction: rtl ? 'rtl' : 'ltr',
  };
  const labelStyle = { fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.25rem', display: 'block' };

  return (
    <EnterpriseModal
      isOpen={isOpen}
      onClose={handleClose}
      size="sm"
      dataTestId="new-client-modal"
      title={t('therapy.clientQuickAdd.title')}
      isSubmitting={submitting}
      footer={
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              border: '1px solid var(--color-border, #d1d5db)',
              background: 'transparent',
              cursor: 'pointer',
            }}
          >
            {t('therapy.clientQuickAdd.cancel')}
          </button>
          <button
            type="submit"
            form="client-quick-add-form"
            data-testid="new-client-submit"
            disabled={submitting}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              border: 'none',
              background: 'var(--color-primary, #0ea5e9)',
              color: 'var(--color-on-primary, #fff)',
              cursor: submitting ? 'default' : 'pointer',
              opacity: submitting ? 0.6 : 1,
            }}
          >
            {t('therapy.clientQuickAdd.submit')}
          </button>
        </div>
      }
    >
      <form
        id="client-quick-add-form"
        onSubmit={handleSubmit}
        style={{ display: 'grid', gap: '0.75rem', direction: rtl ? 'rtl' : 'ltr' }}
      >
        <div>
          <label style={labelStyle}>{t('therapy.clientQuickAdd.fullName')}</label>
          <input
            data-testid="new-client-fullname"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>{t('therapy.clientQuickAdd.phone')}</label>
          <input
            data-testid="new-client-phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>{t('therapy.clientQuickAdd.email')}</label>
          <input
            data-testid="new-client-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={inputStyle}
          />
        </div>
        {error && <div style={{ color: '#ef4444', fontSize: '0.8125rem' }}>{error}</div>}
      </form>
    </EnterpriseModal>
  );
}
